// =====================================================================
// Eventi RMQ del dominio STATO COLONNINE (RFC stato-colonnine, fase 2a.3).
// Contratto condiviso backend <-> evse-state-service. Solo definizioni:
// il wiring (assertExchange/queue, plugin) è fase 2b.
// =====================================================================

import { BaseEvent } from './events';
import {
  PhysicalStatusValues,
  SessionStateValues,
  AggregateStateValues,
} from '../enums/evseState.enum';
import { StateValues } from '../enums/state.enum';

// ---------------------------------------------------------------------
// EXCHANGES
// ---------------------------------------------------------------------

/** Topic exchange del dominio stato (pub/sub bidirezionale backend/service). */
export const EVSE_STATE_EXCHANGE = 'evse.state.events';

/**
 * Exchange CONSISTENT-HASH per lo stream fisico ordinato (RFC §3). Richiede il
 * plugin `rabbitmq_consistent_hash_exchange`. Publish con "routing key" = stationId:
 * l'exchange hasha la key e manda tutti gli eventi di una stazione alla STESSA coda
 * → stessa istanza, in ordine. I tipi di evento si distinguono col campo `type` nel
 * body (con x-consistent-hash la routing key serve solo all'hash, non al filtro).
 */
export const EVSE_INGEST_EXCHANGE = 'evse.ingest';
export const evseIngestRoutingKey = (stationId: string): string => stationId;

// ---------------------------------------------------------------------
// STREAM FISICO (→ EVSE_INGEST_EXCHANGE, ordinato per stationId)
// physicalStatus è GIÀ normalizzato dal sanitization layer: sul bus solo dati puliti.
// ---------------------------------------------------------------------

export type EvsePhysicalEventType =
  | 'evse.innerstate.observed'
  | 'station.online.changed';

export interface EvseInnerStateObservedEvent extends BaseEvent {
  type: 'evse.innerstate.observed';
  stationId: string;
  connectorId: number;
  cpConnectorId?: number | null;
  physicalStatus: PhysicalStatusValues;
  evseId?: string | null;
  errorCode?: string | null;
}

export interface StationOnlineChangedEvent extends BaseEvent {
  type: 'station.online.changed';
  stationId: string;
  online: boolean;
}

export type EvsePhysicalEvent = EvseInnerStateObservedEvent | StationOnlineChangedEvent;

// ---------------------------------------------------------------------
// STATO SESSIONE (backend → service + interno) — EVSE_STATE_EXCHANGE
// ---------------------------------------------------------------------

export interface SessionStateChangedEvent extends BaseEvent {
  sessionId: string;
  connectorId: number | null;
  fromState: SessionStateValues | null; // null = creazione
  toState: SessionStateValues;
  reason?: string;
}
export const sessionStateChangedRoutingKey = (sessionId: string): string =>
  `${sessionId}.session.state.changed`;
export const SESSION_STATE_CHANGED_PATTERN = '*.session.state.changed';

// ---------------------------------------------------------------------
// CONNETTIVITÀ GATEWAY (RFC §5.6, unifica OCPP/OCPI/OICP) — EVSE_STATE_EXCHANGE
// ---------------------------------------------------------------------

export interface GatewayConnectivityChangedEvent extends BaseEvent {
  kind: 'ocpp' | 'ocpi' | 'oicp';
  gatewayId: string; // number (ocpp/oicp) o partyId (ocpi), uniformato a string
  online: boolean;
}
export const gatewayConnectivityRoutingKey = (gatewayId: string): string =>
  `${gatewayId}.gateway.connectivity.changed`;
export const GATEWAY_CONNECTIVITY_PATTERN = '*.gateway.connectivity.changed';

// ---------------------------------------------------------------------
// ENTITY DELETED (soft-remove connector/station/installation) — EVSE_STATE_EXCHANGE
// Chiude il pin-fantasma: oggi il softRemove non emette nulla (RFC red-team #12).
// ---------------------------------------------------------------------

export interface EntityDeletedEvent extends BaseEvent {
  entity: 'connector' | 'station' | 'installation';
  entityId: string; // uniformato a string (connectorId numerico → string)
}
export const entityDeletedRoutingKey = (entityId: string): string =>
  `${entityId}.entity.deleted`;
export const ENTITY_DELETED_PATTERN = '*.entity.deleted';

// ---------------------------------------------------------------------
// AGGREGATO INSTALLAZIONE (service → backend, per il relay socket.io rooms §5.5)
// ---------------------------------------------------------------------

export interface InstallationAggregateChangedEvent extends BaseEvent {
  installationId: string;
  aggregateState: AggregateStateValues;
}
export const installationAggregateChangedRoutingKey = (installationId: string): string =>
  `${installationId}.installation.aggregate.changed`;
export const INSTALLATION_AGGREGATE_CHANGED_PATTERN = '*.installation.aggregate.changed';

/**
 * AGGREGATO STAZIONE (service → backend), per la sola scheda aperta.
 *
 * Serve perché l'aggregato dell'INSTALLAZIONE può non cambiare quando cambia quello di una sua
 * stazione (basta che un'altra resti libera), e perché un guasto CP-level (`connectorId = 0`) porta
 * la stazione a UNAVAILABLE senza toccare nessun connettore — quindi non produce nemmeno un
 * `connector.state.changed`. Senza questo evento quel caso non arriverebbe da nessuna parte.
 * Edge-triggered come l'aggregato installazione: si emette solo al cambio reale.
 */
export interface StationAggregateChangedEvent extends BaseEvent {
  stationId: string;
  installationId: string;
  aggregateState: AggregateStateValues;
}
export const stationAggregateChangedRoutingKey = (stationId: string): string =>
  `${stationId}.station.aggregate.changed`;
export const STATION_AGGREGATE_CHANGED_PATTERN = '*.station.aggregate.changed';

// ---------------------------------------------------------------------
// STATO LOGICO CONNETTORE (service → backend): il service è l'unico a calcolare lo stato
// (fusione fisico⊕sessione, computeConnectorState) e lo COMUNICA; il backend lo persiste
// dumb su `connector.state` (nessun calcolo nel backend, la state machine sta nel service).
// ---------------------------------------------------------------------

export interface ConnectorStateChangedEvent extends BaseEvent {
  connectorId: number;
  state: StateValues;
}
export const connectorStateChangedRoutingKey = (connectorId: number): string =>
  `${connectorId}.connector.state.changed`;
export const CONNECTOR_STATE_CHANGED_PATTERN = '*.connector.state.changed';

// ---------------------------------------------------------------------
// COMANDI SESSIONE (service → backend esecutore, RFC §6.1). Sono RICHIESTE:
// il backend valida contro la matrice, sessione già chiusa → no-op idempotente.
// ---------------------------------------------------------------------

export interface SessionStartRequestedEvent extends BaseEvent {
  connectorId: number;
}
export const sessionStartRequestedRoutingKey = (connectorId: number): string =>
  `${connectorId}.session.start.requested`;
export const SESSION_START_REQUESTED_PATTERN = '*.session.start.requested';

export interface SessionOccupationStartedEvent extends BaseEvent {
  sessionId: string;
  connectorId: number;
}
export const sessionOccupationStartedRoutingKey = (sessionId: string): string =>
  `${sessionId}.session.occupation.started`;
export const SESSION_OCCUPATION_STARTED_PATTERN = '*.session.occupation.started';

export interface SessionChargingResumedEvent extends BaseEvent {
  sessionId: string;
  connectorId: number;
}
export const sessionChargingResumedRoutingKey = (sessionId: string): string =>
  `${sessionId}.session.charging.resumed`;
export const SESSION_CHARGING_RESUMED_PATTERN = '*.session.charging.resumed';

export interface SessionEndRequestedEvent extends BaseEvent {
  sessionId: string;
  reason: string;
}
export const sessionEndRequestedRoutingKey = (sessionId: string): string =>
  `${sessionId}.session.end.requested`;
export const SESSION_END_REQUESTED_PATTERN = '*.session.end.requested';

/**
 * GUASTO DELLA COLONNINA con sessioni vive sopra (CP-level `connectorId = 0` che entra in
 * `STATION_FAULT_PHYSICAL`). Distinto da `session.end.requested` perché la semantica è diversa:
 * lì il fisico dice che la ricarica è finita e si chiudono i conti; qui la colonnina si è rotta
 * SOTTO una sessione viva, e prima di chiudere bisogna PROVARE A FERMARE IL DEVICE — se accetta si
 * chiude e si libera il connettore, se rifiuta la sessione resta appesa e lo si scrive in timeline.
 * Sovraccaricare `session.end.requested` avrebbe cambiato anche il path Faulted a livello connettore,
 * che oggi chiude senza parlare col device.
 *
 * `connectorId` serve all'executor per risolvere la transazione OCPP da fermare.
 */
export interface SessionFaultStopRequestedEvent extends BaseEvent {
  sessionId: string;
  connectorId: number;
  stationId: string;
  reason: string;
  /** `errorCode` OCPP del guasto CP-level, se la colonnina l'ha mandato. Va in timeline. */
  errorCode?: string | null;
}
export const sessionFaultStopRequestedRoutingKey = (sessionId: string): string =>
  `${sessionId}.session.fault.stop.requested`;
export const SESSION_FAULT_STOP_REQUESTED_PATTERN = '*.session.fault.stop.requested';

// ---------------------------------------------------------------------
// QUEUE (convention q.<service>.<scopo>) + DLQ
// ---------------------------------------------------------------------

// evse-state-service ← stream fisico. In 2b: UNA coda per istanza, bound al
// consistent-hash exchange (questo è il nome base, l'istanza appende un suffisso).
export const Q_EVSE_STATE_INGEST = 'q.evse-state-service.ingest';
export const Q_EVSE_STATE_INGEST_DLQ = 'q.evse-state-service.ingest.dlq';

// evse-state-service ← eventi topic che gli servono per l'aggregato
export const Q_EVSE_STATE_SESSION = 'q.evse-state-service.session-state';
export const Q_EVSE_STATE_SESSION_DLQ = 'q.evse-state-service.session-state.dlq';
export const Q_EVSE_STATE_GATEWAY = 'q.evse-state-service.gateway-connectivity';
export const Q_EVSE_STATE_GATEWAY_DLQ = 'q.evse-state-service.gateway-connectivity.dlq';
export const Q_EVSE_STATE_ENTITY_DELETED = 'q.evse-state-service.entity-deleted';
export const Q_EVSE_STATE_ENTITY_DELETED_DLQ = 'q.evse-state-service.entity-deleted.dlq';

// backend ← comandi sessione (esecutore)
export const Q_BACKEND_SESSION_COMMANDS = 'q.backend.session-commands';
export const Q_BACKEND_SESSION_COMMANDS_DLQ = 'q.backend.session-commands.dlq';

// backend ← installation.aggregate.changed (relay rooms)
export const Q_BACKEND_AGGREGATE_CHANGED = 'q.backend.aggregate-changed';
export const Q_BACKEND_AGGREGATE_CHANGED_DLQ = 'q.backend.aggregate-changed.dlq';

// backend ← connector.state.changed (persist dumb su connector.state)
export const Q_BACKEND_CONNECTOR_STATE = 'q.backend.connector-state';
export const Q_BACKEND_CONNECTOR_STATE_DLQ = 'q.backend.connector-state.dlq';
